import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    FormGroup,
    Label,
    Input,
} from 'reactstrap';
import { useEffect, useMemo, useState, useContext } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../context';
import Select from 'react-select'; // TODO: ???
import '../Daylight.css';
import hasPermission from '../../../Shared/hasPermission';
import Fuse from 'fuse.js';
import debounce from 'lodash.debounce';
import { DaylightContext } from '../DaylightCodeMapperModal/DaylightCodeMapper';

const DaylightEditMappingModal = ({ practiceId }) => {
    const dispatch = useDispatch();
    const isOpen = useSelector(
        (state) => state.daylight.isEditMappingModalOpen
    );
    // TODO: do proper diffing
    const incomingNeoCodes = useSelector(
        (state) => state.daylight.incomingNeoCodes,
        shallowEqual
    );
    const selectedCode = useSelector(
        (state) => state.daylight.actionTargetCode,
        shallowEqual
    );
    const selectedMapping = useSelector(
        (state) => state.daylight.actionTargetMapping,
        shallowEqual
    );
    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );
    const canWriteComments = hasPermission(
        'CodeMapsCommentsWrite',
        permissions
    );

    const { partnerId } = useContext(DaylightContext);

    const [mappingToConfirm, setMappingToConfirm] = useState(null);
    const [commentsText, setCommentsText] = useState(
        selectedMapping?.comments ?? ''
    );
    //handle searching
    const [inputValue, setInputValue] = useState('');
    const [filteredNeoCodes, setFilteredNeoCodes] = useState(incomingNeoCodes);

    useEffect(() => {
        const fuseOptions = {
            keys: [
                { name: 'code', weight: 1.0 },
                { name: 'label', weight: 0.5 },
            ],
            location: 50,
            distance: 200,
            threshold: 0.5,
        };

        const fuse = new Fuse(incomingNeoCodes, fuseOptions);

        const debouncedSearch = debounce((value) => {
            const results = value
                ? fuse.search(value)
                : incomingNeoCodes.map((code) => ({ item: code }));
            setFilteredNeoCodes(results.map((result) => result.item));
        }, 300);

        debouncedSearch(inputValue);
        return () => debouncedSearch.cancel();
    }, [incomingNeoCodes, inputValue]);

    const handleInputChange = (value) => {
        setInputValue(value);
    };

    const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const filteredOptions = useMemo(() => {
        if (inputValue === '') {
            return [
                ...filteredNeoCodes
                    .slice(0, 50)
                    .filter(
                        (x) =>
                            (selectedCode?.mappings || []).filter(
                                (y) => y.taxonomyCodeNodeGuid === x.guid
                            ).length === 0
                    )
                    .map((x) => {
                        return { label: `[${x.code}] ${x.label}`, value: x };
                    }),
                {
                    label: 'Search to see more codes...',
                    value: null,
                    isDisabled: true,
                },
            ];
        }
        const escapedInputValue = escapeRegExp(inputValue);
        const regByInclusion = new RegExp(escapedInputValue, 'i');
        return [
            ...filteredNeoCodes
                .filter(
                    (x) =>
                        (selectedCode?.mappings || []).filter(
                            (y) => y.taxonomyCodeNodeGuid === x.guid
                        ).length === 0
                )
                .filter(
                    (x) =>
                        regByInclusion.test(x.label) ||
                        regByInclusion.test(x.code)
                )
                .slice(0, 100)
                .map((x) => {
                    return { label: `[${x.code}] ${x.label}`, value: x };
                }),
            {
                label: 'Search to see more codes...',
                value: null,
                isDisabled: true,
            },
        ];
        // eslint-disable-next-line
    }, [inputValue, filteredNeoCodes, selectedCode]);

    const confirm = () => {
        const payload = {
            taxonomyCodeNodeGuid: mappingToConfirm?.value.guid,
            taxonomyCodeNodeLabel: mappingToConfirm?.value.label,
            taxonomyCodeNodeType: mappingToConfirm?.value.nodeType,
            codeName: mappingToConfirm?.value.code,
            codeType: mappingToConfirm?.value.type,
            unitCost: mappingToConfirm?.value.unitCost,
            buyingQuantity: mappingToConfirm?.value.buyingQuantity,
            sellingQuantity: mappingToConfirm?.value.sellingQuantity,
            oldTaxonomyCodeNodeGuid: selectedMapping?.taxonomyCodeNodeGuid,
            comments: commentsText,
        };

        if (canWriteComments && commentsText !== selectedMapping?.comments) {
            dispatch(
                actions.daylight.editDaylightMappingWithComments(
                    practiceId,
                    partnerId,
                    selectedCode,
                    selectedMapping.id,
                    payload
                )
            );
        } else {
            dispatch(
                actions.daylight.editDaylightMapping(
                    practiceId,
                    partnerId,
                    selectedCode,
                    selectedMapping.id,
                    payload
                )
            );
        }

        closeModal();
    };

    const closeModal = () => {
        setMappingToConfirm(null);
        dispatch(actions.daylight.setActionTargetCode({}));
        dispatch(actions.daylight.setActionTargetMapping({}));
        dispatch(actions.daylight.setEditMappingModalOpen(false));
    };

    return (
        <Modal
            centered={true}
            isOpen={isOpen}
            className="text-light"
            style={{ minWidth: '40vw' }}>
            <ModalHeader>
                Change Map [
                {selectedMapping?.codeName ||
                    selectedMapping?.taxonomyCodeNodeLabel}
                ]&nbsp;
                {selectedMapping?.taxonomyCodeNodeLabel}
            </ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label>Mapping:</Label>
                    {/* error in the console for this UNSAFE_componentWillReceiveProps used */}
                    <Select
                        className="text-dark"
                        options={filteredOptions}
                        onChange={(e) => setMappingToConfirm(e)}
                        onInputChange={handleInputChange}
                        filterOption={() => true} // disable native filter
                        isClearable={true}
                    />
                </FormGroup>
                {canWriteComments && (
                    <FormGroup className="mt-3">
                        <Label>{'Comments (Optional):'}</Label>
                        <Input
                            type="textarea"
                            rows="8"
                            value={commentsText}
                            maxLength={800}
                            onChange={(e) => setCommentsText(e.target.value)}
                        />
                    </FormGroup>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="info" onClick={closeModal}>
                    Cancel
                </Button>
                <Button
                    color="success"
                    disabled={!mappingToConfirm?.value}
                    onClick={confirm}>
                    Change
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DaylightEditMappingModal;
